import {
  Grid,
  GridItem,
  IconButton,
  InputLeftElement,
  Spacer,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { LineItemType, SurchargeUnit } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  costUnitTypeOptions,
  getPrettyLineItemType,
  lineItemTypeOptions,
  modelUtils,
  surchargeUnitOptions,
} from '@pelicargo/utils';
import { ChangeEvent, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HiTrash } from 'react-icons/hi2';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { IoReturnDownForward } from 'react-icons/io5';
import { Link as RouterLink } from 'react-router-dom';

import { TrpcOutputs } from '../../config/trpc';
import { Patch } from '../../hooks/lineItem/useBulkUpdateRequestLineItems';
import { useCreateLineItem } from '../../hooks/lineItem/useCreate';
import { useDeleteLineItem } from '../../hooks/lineItem/useDelete';
import { useLineItemForm } from '../../hooks/lineItem/useForm';
import { useUpdateLineItem } from '../../hooks/lineItem/useUpdate';
import { QuoteLineItem } from '../../utils/types';
import { ActionMenu } from '../ActionMenu';
import { ConnectionPopover } from '../ConnectionPopover';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';

type BulkUpdateFn = (
  sourceLineItemId: number,
  peerIds: number[],
  patch: Patch,
) => Promise<TrpcOutputs['bulkUpdateRequestLineItems']>;

type LineItemRowProps = {
  lineItem: QuoteLineItem;
  peerIds?: number[];
  isDisabled?: boolean;
  isByRequest?: boolean;
  onBulkUpdate?: BulkUpdateFn;
  requestId?: number;
  quoteCategory?: 'airline' | 'inland';
  quoteLabel?: string;
  quoteVariantId?: number;
};
const VALID_TYPES_FOR_MARKUP_PARENT: LineItemType[] = [
  LineItemType.CONTRACT,
  LineItemType.PROMO,
  LineItemType.TARIFF,
  LineItemType.SPOT,
  LineItemType.PICK_UP,
  LineItemType.TRANSFER,
];

export const LineItemRow = ({
  lineItem,
  isDisabled,
  peerIds = [],
  isByRequest = false,
  onBulkUpdate,
  requestId,
  quoteCategory,
  quoteLabel,
  quoteVariantId,
}: LineItemRowProps) => {
  const [updateLineItem, isUpdating] = useUpdateLineItem(lineItem?.id);
  const [deleteLineItem, isDeleting] = useDeleteLineItem(lineItem?.id);
  const { methods } = useLineItemForm(lineItem);
  const { createDefault: createMarkup, isLoading: isCreatingMarkup } = useCreateLineItem(lineItem?.quote_variant_id);
  const label = methods.watch('label');
  const toast = useToast();

  const isChild = useMemo(() => !!lineItem?.parent_id, [lineItem?.parent_id]);

  const fadedColor = useColorModeValue('gray.500', 'gray.400');
  const defaultColor = useColorModeValue('white', 'gray.600');

  const perUnitCost = lineItem.per_unit_cost;
  const isSpot = lineItem.type === LineItemType.SPOT;

  // in dollars since per_unit_cost was converted to dollars after getting it from server
  const isRowFlagged =
    modelUtils.lineItem.isRateCategory(lineItem.type) && (perUnitCost > 10 || (isSpot && perUnitCost < 0.25));
  const bgColor = isRowFlagged ? 'red' : defaultColor;

  const onSelectType = (event: ChangeEvent<HTMLSelectElement>) => {
    if (label) return;

    const type = event.target.value;
    const value = getPrettyLineItemType(type as LineItemType);
    methods.setValue('label', value);
  };

  const handleCreateMarkup = async () => {
    await createMarkup({ parent_id: lineItem.id, type: LineItemType.FULFILLMENT_PARTNER_MARKUP });
  };

  const renderLabelLeftElement = (isChild: boolean) => {
    if (!isChild || isByRequest) return null;

    return (
      <InputLeftElement pointerEvents="none">
        <IoReturnDownForward size={20} />
      </InputLeftElement>
    );
  };

  const handleUpdateLineItem = async (formValues: any) => {
    if (isDisabled) return;

    if (isByRequest && onBulkUpdate) {
      await onBulkUpdate(lineItem.id, peerIds, formValues);
      return;
    }

    const { warning } = await updateLineItem(formValues);

    if (warning) toast({ title: warning, status: 'warning', duration: 5000, isClosable: true });
  };

  const isRateType = modelUtils.lineItem.isRateCategory(lineItem.type);
  const isPercentage = modelUtils.lineItem.isPercentage(lineItem?.per_unit_cost_type);

  // We currently only allow markups to be added to certain types of line items
  const shouldShowActionMenu = !lineItem?.parent_id && VALID_TYPES_FOR_MARKUP_PARENT.includes(lineItem.type);
  const shouldShowQuoteVariantLink = isByRequest;
  const isQuoteVariantLinkDisabled = !quoteVariantId || !requestId || peerIds.length > 1;
  const quoteVariantPath = quoteCategory === 'inland' ? 'inland' : 'airline';
  const quoteVariantLink =
    requestId && quoteVariantId ? `/requests/${requestId}/${quoteVariantPath}#QV${quoteVariantId}` : undefined;

  return (
    <Grid
      templateColumns="repeat(28, 1fr)"
      w="full"
      borderTopWidth={1}
      gap={0}
      bgColor={bgColor}
      className="line-item"
      _last={{ borderBottomWidth: 1 }}
    >
      <SimpleForm onValid={handleUpdateLineItem} defaultValues={lineItem} {...(methods as UseFormReturn<any>)}>
        <GridItem colSpan={1}>
          <ConnectionPopover lineItem={lineItem} />
        </GridItem>
        <Show if={isByRequest}>
          <GridItem colSpan={4} alignContent="center">
            <Text px={2} fontSize="sm" color={fadedColor} noOfLines={1}>
              {isByRequest ? quoteLabel : '-'}
            </Text>
          </GridItem>
        </Show>
        <GridItem colSpan={isByRequest ? 6 : 8} alignContent="center">
          <SimpleInput
            name="label"
            isDisabled={isDisabled}
            leftElement={renderLabelLeftElement(isChild)}
            inputAttrs={{
              borderRadius: 0,
              borderWidth: 0,
              bg: bgColor,
            }}
          />
        </GridItem>
        <GridItem colSpan={isByRequest ? 5 : 6} alignContent="center">
          <SimpleSelect
            name="type"
            size="md"
            variant="filled"
            isDisabled={isDisabled}
            borderRadius={0}
            optionGroupOptions={lineItemTypeOptions}
            onChange={onSelectType}
            selectProps={{
              borderRadius: 0,
              borderWidth: 0,
              bg: bgColor,
              icon: <></>,
            }}
          />
        </GridItem>
        <GridItem colSpan={3} alignContent="center">
          <Show if={!isPercentage}>
            <SimpleInput
              name="per_unit_count"
              inputAttrs={{
                isDisabled:
                  isDisabled ||
                  isUpdating ||
                  isChild ||
                  isPercentage ||
                  lineItem.per_unit_count_type === SurchargeUnit.FLAT,
                borderRadius: 0,
                borderWidth: 0,
                textAlign: 'right',
                bg: bgColor,
              }}
              rightElement={
                <SimpleSelect
                  name="per_unit_count_type"
                  size="md"
                  variant="filled"
                  borderRadius={0}
                  placeholder=""
                  flex={0}
                  w="36px"
                  options={surchargeUnitOptions}
                  isDisabled={isDisabled || isUpdating || isChild || isPercentage}
                  selectProps={{
                    borderRadius: 0,
                    borderWidth: 0,
                    bg: bgColor,
                    icon: <></>,
                    sx: {
                      paddingLeft: 1,
                      paddingRight: 1,
                      width: 'unset',
                      textAlign: 'center',
                    },
                  }}
                />
              }
            />
          </Show>
          <Show if={isPercentage}>
            <Text w="full" textAlign="right" pr={2} color={fadedColor}>
              -
            </Text>
          </Show>
        </GridItem>
        <GridItem colSpan={4}>
          <SimpleInput
            name="per_unit_cost"
            inputAttrs={{
              isDisabled,
              borderRadius: 0,
              borderWidth: 0,
              textAlign: 'right',
              bg: bgColor,
            }}
            isDisabled={isDisabled || isUpdating}
            rightElement={
              <SimpleSelect
                name="per_unit_cost_type"
                size="md"
                variant="filled"
                borderRadius={0}
                placeholder=""
                hideError
                flex={0}
                w="21px"
                options={costUnitTypeOptions}
                onChange={onSelectType}
                selectProps={{
                  isDisabled: isDisabled || isUpdating || isRateType,
                  borderRadius: 0,
                  borderWidth: 0,
                  bg: bgColor,
                  icon: <></>,
                  sx: {
                    paddingLeft: 1,
                    paddingRight: 1,
                    width: 'unset',
                    textAlign: 'center',
                  },
                }}
              />
            }
          />
        </GridItem>
        <GridItem colSpan={isByRequest ? 3 : 4} alignContent="center">
          <SimpleInput
            name="cost"
            inputAttrs={{
              isDisabled,
              borderRadius: 0,
              borderWidth: 0,
              textAlign: 'right',
              bg: bgColor,
            }}
            isDisabled={isUpdating}
            color={lineItem?.is_per_unit ? fadedColor : undefined}
          />
        </GridItem>
        <GridItem colSpan={1} alignContent="center">
          <Show if={shouldShowActionMenu}>
            <ActionMenu
              isLoading={isCreatingMarkup}
              actions={[{ label: 'Add FP Markup', icon: <IoReturnDownForward />, onClick: handleCreateMarkup }]}
            />
          </Show>
          <Show if={!shouldShowActionMenu}>
            <Spacer w="32px" />
          </Show>
        </GridItem>
        <GridItem colSpan={1} alignContent="center">
          <Show if={!isByRequest}>
            <IconButton
              aria-label="Delete"
              icon={<HiTrash />}
              variant="ghost"
              colorScheme="red"
              opacity={0}
              sx={{ '.line-item:hover &': { opacity: 1 } }}
              isLoading={isDeleting}
              isDisabled={isDisabled}
              onClick={deleteLineItem}
            />
          </Show>
          <Show if={shouldShowQuoteVariantLink}>
            <IconButton
              as={isQuoteVariantLinkDisabled ? undefined : RouterLink}
              to={isQuoteVariantLinkDisabled ? undefined : quoteVariantLink}
              aria-label="Open Quote Variant"
              icon={<HiArrowTopRightOnSquare />}
              variant="ghost"
              colorScheme="blue"
              opacity={0}
              sx={{
                '.line-item:hover &': { opacity: 1 },
                '&:disabled': { opacity: 0 },
                '&:hover:disabled': { opacity: 1 },
              }}
              title={isQuoteVariantLinkDisabled ? 'Links to multiple quote variants' : undefined}
              isDisabled={isQuoteVariantLinkDisabled}
            />
          </Show>
          <Show if={isByRequest && !shouldShowQuoteVariantLink}>
            <Spacer w="32px" />
          </Show>
        </GridItem>
      </SimpleForm>
    </Grid>
  );
};
